// src/components/studio/StudioStep.tsx
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Lock } from "lucide-react";
import { StepConfig } from "../../types/studio.types";

interface StudioStepProps {
  step: StepConfig;
  isActive: boolean;
  status: "completed" | "incomplete" | "locked";
  canNavigate: boolean;
  isMobile: boolean;
  onClick: () => void;
  children?: React.ReactNode;
}

export default function StudioStep({
  step,
  isActive,
  status,
  canNavigate,
  isMobile,
  onClick,
  children,
}: StudioStepProps) {
  const isCompleted = status === "completed";

  return (
    <motion.div
      layout
      onClick={canNavigate && !isActive ? onClick : undefined}
      animate={{
        width: isMobile
          ? isActive
            ? "100%"
            : "90vw"
          : isActive
            ? "65%"
            : "12%",
        height: isMobile
          ? isActive
            ? "60vh"
            : "6vh"
          : isActive
            ? "100%"
            : "50%",
      }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className={`relative rounded-3xl border border-white/30 bg-white/10 backdrop-blur-3xl flex flex-col transition-all duration-300 overflow-hidden
        ${isActive ? "shadow-2xl z-20" : "hover:border-white/50 z-10"}
        ${!canNavigate ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
      `}
    >
      <AnimatePresence mode="wait">
        {isActive ? (
          <motion.div
            key="active-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="h-full"
          >
            {children}
          </motion.div>
        ) : (
          <motion.div
            key="inactive-content"
            className={`h-full flex ${isMobile ? "flex-row px-6 justify-center" : "flex-col py-10 justify-between"} items-center`}
          >
            <div
              className={`flex items-center gap-4 ${isMobile ? "" : "flex-col"}`}
            >
              <div
                className={`p-2.5 rounded-xl border ${isCompleted ? "bg-green-500/10 border-green-500/40 text-green-500" : "bg-white/5 border-white/10 text-white/30"}`}
              >
                {isCompleted ? (
                  <CheckCircle2 size={16} />
                ) : !canNavigate ? (
                  <Lock size={16} />
                ) : (
                  <step.icon size={16} />
                )}
              </div>
              <p
                className={`text-[10px] font-black uppercase tracking-[0.3em] ${isMobile ? "" : "vertical-text"}`}
              >
                {step.title}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
